import { isEmpty } from 'lodash-es';
import shortUUID from 'short-uuid';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { BranchStepMemoryAction, BranchStepMemoryActionType, BranchStepMemoryRoll, BranchStepMemoryRollType } from '../../../dorian-shared/types/branch/BranchStep';
import {
  CheckOperator,
  MemoryArrayLink,
  MemoryCheckValueType,
  MemoryDTO,
  MemoryShowIn,
  MemorySingleLink,
  MemoryType,
  PlayerHasMatchType,
  StepCheck,
  StepCheckOperatorType,
  StepCheckSwitch,
} from '../../../components/pages/Book/MemoryBank/memoryBankTypes';
import {
  isCharacterMemoryToken,
  stripCharacterMemoryPrefix,
} from '../utils/characterMemoryPrefix';
import { resolveCharacterTypeValue } from '../utils/resolveCharacterTypeValue';
import { EpisodeTextImportCommandKey, TEpisodeTextImportCommands } from '../types/episodeTextImportCommandTypes';
import { EpisodeTextImportCrossCommands } from '../types/EpisodeTextImportCrossCommands';
import {
  EpisodeTextColumnKey,
  EpisodeTextEntityType,
  EpisodeTextImportStepValidationConfig,
} from '../types/episodeTextImportTypes';
import { EpisodeTextImportHelper } from './EpisodeTextImportHelper';
import { EpisodeTextImportNode } from './EpisodeTextImportNode';
import { parseReqCommand } from './parseReqCommand';
import { parseRealId } from '../utils/parseRealId';
import { DiceRoll } from '@dice-roller/rpg-dice-roller';

export class EpisodeTextImportStep {
  public id: string;
  public type: EpisodeTextEntityType | undefined = undefined;
  public check: StepCheck | undefined = undefined;
  public switch: StepCheckSwitch[] | undefined = undefined;
  public action: BranchStepMemoryAction | undefined = undefined;
  public roll: BranchStepMemoryRoll | undefined = undefined;
  public speakerName: string;
  public speakerMemoryId: number | undefined = undefined;
  public speakerText: string;
  public description: string;
  public errors: string[] = [];
  public commands: TEpisodeTextImportCommands = new Map();
  public conditionListItems: EpisodeTextImportStep[] = []; // For condition list check items
  public realStepId: number | undefined;
  public realAnswerId: number | undefined;

  constructor(data: string[], stepType: EpisodeTextEntityType) {
    this.realStepId = parseRealId(data[3], '__sid:');
    this.realAnswerId = parseRealId(data[3], '__aid:');
    this.id = this.realStepId != null
      ? `sid:${this.realStepId}`
      : (this.realAnswerId != null ? `aid:${this.realAnswerId}` : shortUUID.generate());
    this.speakerName = data[EpisodeTextColumnKey.NodeOrSpeaker]?.trim() ?? '';
    this.speakerText = data[EpisodeTextColumnKey.Text]?.trim() ?? '';
    this.description = data[EpisodeTextColumnKey.Description]?.trim() ?? '';
    this.type = stepType;
    this.parseCommands();
  }

  /**
   * Re-parses commands from current field values.
   * Call after any direct field mutation to keep `commands` Map in sync.
   * These model objects are mutable by design for interactive edit sessions.
   */
  public reparseCommands(): void {
    this.parseCommands();
  }

  private parseCommands() {
    this.commands = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.NodeOrSpeaker, this.speakerName, new Map());
    this.commands = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.Text, this.speakerText, this.commands);
    this.commands = EpisodeTextImportHelper.parseCommands(EpisodeTextColumnKey.Description, this.description, this.commands);
  }

  public validate(config?: EpisodeTextImportStepValidationConfig): string[] {
    const nodesInImport = config?.nodesInImport ?? [];
    const maxLengthName = config?.maxLengthName ?? 100;
    const maxLengthText = config?.maxLengthText ?? 120;
    const nodesInEpisode = config?.nodesInEpisode ?? [];
    const characterNamesInEpisode = config?.characterNamesInBook ?? [];
    const memoryNames = config?.memoriesInBook ?? [];

    const speakerNamePattern = /^(?:memory\.)?[0-9A-z\-_]+$/i;
    const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    const speakerText = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerText);

    this.errors = [];
    this.speakerMemoryId = undefined;

    this._validateCrossCommands();
    this._validateGotoCommand(nodesInImport, nodesInEpisode);
    this._validateChoiceCommand(nodesInImport);
    this._validateAnswerCommand();
    this._validatePremiumAnswerCommand();
    this._validateChatCommand();
    this._validateRememberCommand(memoryNames, characterNamesInEpisode, nodesInImport);
    this._validateRandomCommand(memoryNames);
    this._validateCheckCommand(memoryNames, characterNamesInEpisode, nodesInImport, nodesInEpisode, config?.rewardIds);
    this._validateReqCommand(memoryNames, characterNamesInEpisode);
    this._validateEndingCommand(nodesInImport);
    this._validateInvalidCommands();

    /* Validate OTHERS */
    if (this.type === EpisodeTextEntityType.EmptyStep) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Row should not be empty`);
    }

    if (speakerName.length > maxLengthName) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Character alias exceeds the limit of ${maxLengthName} characters`);
    }

    if (!isEmpty(speakerName) && !speakerNamePattern.test(speakerName)) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Invalid character alias. Allowed characters: A-Z, a-z, 0-9, _, -`);
    }

    if (speakerText.length > maxLengthText) {
      this.errors.push(`[${EpisodeTextColumnKey.Text}] Speaker text exceeds the limit of ${maxLengthText} characters`);
    }

    this._resolveSpeakerMemoryOrCharacter(memoryNames, characterNamesInEpisode);

    return this.errors;
  }

  private _resolveSpeakerMemoryOrCharacter(memoryBank: MemoryDTO[], characterNamesInEpisode: string[]) {
    const speakerToken = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    if (isEmpty(speakerToken)) return;

    const speakerCheckedTypes: EpisodeTextEntityType[] = [
      EpisodeTextEntityType.Dialogue,
      EpisodeTextEntityType.Choice,
      EpisodeTextEntityType.Thinking,
      EpisodeTextEntityType.Reaction,
      EpisodeTextEntityType.Chat,
    ];
    if (this.type === undefined || !speakerCheckedTypes.includes(this.type)) return;

    if (isCharacterMemoryToken(speakerToken)) {
      const memoryName = stripCharacterMemoryPrefix(speakerToken).toLocaleLowerCase().trim();
      const memory = memoryBank.find((m) => m.name.toLocaleLowerCase().trim() === memoryName);
      if (!memory) {
        this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] CharacterMemory variable does not exist in Memory Bank: ${speakerToken}`);
        return;
      }
      if (memory.type !== MemoryType.Character) {
        this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Memory variable is not of type Character: ${speakerToken}`);
        return;
      }
      this.speakerMemoryId = memory.id;
      return;
    }

    // No memory. prefix → must be a real CharacterSkin alias for Dialogue/Choice.
    if (this.type === EpisodeTextEntityType.Dialogue || this.type === EpisodeTextEntityType.Choice) {
      if (!EpisodeTextImportHelper.isNameExists(this.speakerName, characterNamesInEpisode)) {
        this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Character alias does not exist in the episode`);
      }
    }
  }

  private _validateCrossCommands() {
    const usedCommands = [...this.commands.keys()];
    this.commands.forEach((_, key) => {
      const canBeUsedWithCommands = EpisodeTextImportCrossCommands.commands[key];
      const isError = usedCommands.some((usedCommand) => usedCommand !== key && !canBeUsedWithCommands.includes(usedCommand));
      if (isError) {
        const usedCommandsText = usedCommands.filter((usedCommand) => usedCommand !== key).map((usedCommand) => `#${usedCommand.toString()}`).join(', ');
        this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #${key.toString()} command cannot be used with ${usedCommandsText}`);
      }

      const canBeInEntityType = EpisodeTextImportCrossCommands.entityType[key];
      const isErrorEntityType = !canBeInEntityType.includes(this.type!);
      if (isErrorEntityType) {
        this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #${key.toString()} command cannot be used in ${this.type} step type.`);
      }
    });

    const usedPremiumAnswerCommand = this.commands.get(EpisodeTextImportCommandKey.PremiumAnswer);
    const usedBonusContentCommand = this.commands.get(EpisodeTextImportCommandKey.BonusContent);

    const bonusContentKeys = usedBonusContentCommand ? Array.from(usedBonusContentCommand.keys()) : [];
    bonusContentKeys.forEach((columnKey) => {
      if (!usedPremiumAnswerCommand) {
        this.errors.push(`[${columnKey}] #BONUS command cannot be used without #PREMIUM: command`);
      }
    });
  }

  private _validateGotoCommand(nodesInImport: EpisodeTextImportNode[], nodesInEpisode: Branch[]) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const gotoCommandInColumnKey = commands.goto.keys().next().value;
    const gotoCommandValue = commands.goto.values().next().value?.[2] ?? '';

    if (!gotoCommandInColumnKey) {
      return;
    }

    const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    if (!isEmpty(speakerName)) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #GOTO: Character alias cannot be used with this command`);
    }

    const hasGotoCommandInSpeaker = commands.goto.has(EpisodeTextColumnKey.NodeOrSpeaker);
    const hasGotoCommandInDescription = commands.goto.has(EpisodeTextColumnKey.Description);

    if (hasGotoCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #GOTO: cannot be used in this column`);
    }

    if (hasGotoCommandInDescription && this.type !== EpisodeTextEntityType.Answer && this.type !== EpisodeTextEntityType.CheckCondition) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] #GOTO: command in description should be in ANSWER step type`);
    }

    if (!hasGotoCommandInDescription && this.type === EpisodeTextEntityType.Answer) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] #GOTO: command missing. It should be in ANSWER step type, or make sure you wrote the command correctly`);
    }

    if (isEmpty(gotoCommandValue)) {
      this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The node name should be provided`);
    }

    const existingNodeNames = nodesInImport.map((node) => node.title);
    if (!EpisodeTextImportHelper.isNameExists(gotoCommandValue, [...existingNodeNames, ...nodesInEpisode.map((node) => node.title)])) {
      this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The name does not exist in the importing nodes or the existing episode.`);
    }

    // Extended lookup: also traverse conditionListItems for CheckCondition steps
    const currentNode = nodesInImport.find((node) =>
      node.steps.some((step) =>
        step.id === this.id ||
        step.conditionListItems?.some((c) => c.id === this.id),
      ),
    );
    if (currentNode && gotoCommandValue.toLocaleLowerCase().trim() === currentNode.title.toLocaleLowerCase().trim()) {
      this.errors.push(
        `[${gotoCommandInColumnKey}] #GOTO: Cannot reference the current node "${currentNode.title}" as it would create an infinite loop`,
      );
    }
    if (currentNode) {
      const currentStepIndex = currentNode.steps.findIndex((step) => step.id === this.id);
      // If step is in conditionListItems (not in node.steps), skip position-based validation
      if (currentStepIndex !== -1) {
        const prevStep = currentNode.steps[currentStepIndex - 1];
        const nextStep = currentNode.steps[currentStepIndex + 1];
        if (this.type !== EpisodeTextEntityType.Answer) {
          if (!prevStep) {
            this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The command must not be on the first step of the node`);
          }
          if (nextStep) {
            this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The command must be on the last step of the node`);
          }
        }

        const hasCheckStep = currentNode.steps.some((step) => step.type === EpisodeTextEntityType.Check);
        if (hasCheckStep) {
          this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The command cannot be used with CHECK step type`);
        }
        const hasChoice = currentNode.steps.some((step) => step.type === EpisodeTextEntityType.Choice);
        if (hasChoice && gotoCommandInColumnKey !== EpisodeTextColumnKey.Description) {
          this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The command cannot be used with CHOICE step type`);
        }
        const hasAnswer = currentNode.steps.some((step) => step.type === EpisodeTextEntityType.Answer);
        if (hasAnswer && gotoCommandInColumnKey !== EpisodeTextColumnKey.Description) {
          this.errors.push(`[${gotoCommandInColumnKey}] #GOTO: The command cannot be used with ANSWER step type`);
        }
      }
    }
  }

  private _validateChoiceCommand(nodesInImport: EpisodeTextImportNode[]) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const speakerNameCleared = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    const speakerTextCleared = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerText);

    const hasChoiceCommandInSpeaker = commands.choice.has(EpisodeTextColumnKey.NodeOrSpeaker);
    if (hasChoiceCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #CHOICE: command cannot be used in this cell`);
      return;
    }

    const columnKey = commands.choice.keys().next().value;

    if (!columnKey) {
      return;
    }

    const currentNode = nodesInImport.find((node) => node.steps.some((step) => step.id === this.id));
    if (currentNode) {
      const currentStepIndex = currentNode.steps.findIndex((step) => step.id === this.id);
      const prevStep = currentNode.steps[currentStepIndex - 1];
      const nextStep = currentNode.steps[currentStepIndex + 1];
      const answersCount = currentNode.steps.filter((step) => step.type === EpisodeTextEntityType.Answer).length;

      if (prevStep) {
        this.errors.push(`[${columnKey}] #CHOICE: Choice command should be the first step in the node`);
        return;
      }

      if (nextStep?.type === EpisodeTextEntityType.Answer) {
        if (answersCount !== currentNode.steps.length - 1) {
          this.errors.push(`[${columnKey}] #CHOICE: only ANSWER steps should be present`);
        }
        if (answersCount > 3) {
          this.errors.push(`[${columnKey}] #CHOICE: Only THREE answers should be present`);
        }

        // Check for self-reference in answer steps
        const answerSteps = currentNode.steps.filter((step) => step.type === EpisodeTextEntityType.Answer);
        const hasSelfReference = answerSteps.some((answerStep) => {
          const answerCommands = EpisodeTextImportHelper.convertCommandsToRecordType(answerStep.commands);
          const gotoCommandValue = answerCommands.goto.values().next().value?.[2] ?? '';
          return gotoCommandValue && currentNode.title.toLocaleLowerCase().trim() === gotoCommandValue.toLocaleLowerCase().trim();
        });

        if (hasSelfReference) {
          this.errors.push(`[${columnKey}] #CHOICE: Answer cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
        }
      } else {
        if (!nextStep) {
          this.errors.push(`[${columnKey}] #CHOICE: ] At least ONE answer step should be present`);
          return;
        }
        this.errors.push(`[${columnKey}] #CHOICE: Only ANSWER steps should be present`);
        return;
      }
    }

    if (isEmpty(speakerNameCleared)) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Choice step should have a character alias`);
    }
    if (speakerTextCleared.length > 40) {
      this.errors.push(`[${EpisodeTextColumnKey.Text}] Choice text exceeds the limit of 40 characters`);
    }
  }

  private _validateAnswerCommand() {
    const isAnswerStepType = this.type === EpisodeTextEntityType.Answer;

    if (!isAnswerStepType) {
      return;
    }

    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const speakerNameCleared = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    const hasGotoCommand = commands.goto.size > 0;

    if (!isEmpty(speakerNameCleared)) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Answer step should not have a character alias`);
    }

    if (!hasGotoCommand) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] #GOTO: command should be used`);
    }
  }

  private _validatePremiumAnswerCommand() {
    if (this.type !== EpisodeTextEntityType.Answer) {
      return;
    }
    if (!this.commands.has(EpisodeTextImportCommandKey.PremiumAnswer)) {
      return;
    }

    const premiumAnswerCommand = this.commands.get(EpisodeTextImportCommandKey.PremiumAnswer);
    const hasPremiumAnswerCommandInSpeaker = premiumAnswerCommand?.get(EpisodeTextColumnKey.NodeOrSpeaker);

    const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
    if (!isEmpty(speakerName)) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Answer step should not have a character alias`);
      return;
    }

    const speakerText = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerText);
    if (speakerText.length > 40) {
      this.errors.push(`[${EpisodeTextColumnKey.Text}] Answer text exceeds the limit of 40 characters`);
      return;
    }

    if (hasPremiumAnswerCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #PREMIUM: command cannot be used in this cell`);
      return;
    }

    const premiumAnswerValue = premiumAnswerCommand?.values().next().value?.[2] ?? '';
    if (isEmpty(premiumAnswerValue)) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] #PREMIUM: value should be provided`);
      return;
    }
    if (Number.isNaN(Number(premiumAnswerValue))) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] #PREMIUM: value should be a number. Value: ${premiumAnswerValue}`);
    }
  }

  private _validateChatCommand() {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);

    const hasChatCommandInSpeaker = commands.chat.has(EpisodeTextColumnKey.NodeOrSpeaker);
    const hasChatCommandInText = commands.chat.has(EpisodeTextColumnKey.Text);
    const hasChatCommandInDescription = commands.chat.has(EpisodeTextColumnKey.Description);

    if (hasChatCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #CHAT: command should be in the DESCRIPTION column`);
    }
    if (hasChatCommandInText) {
      this.errors.push(`[${EpisodeTextColumnKey.Text}] #CHAT: command should be in the DESCRIPTION column`);
    }

    if (hasChatCommandInDescription
      && this.type !== EpisodeTextEntityType.Chat) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #CHAT: command cannot be used in this step`);
    }
  }

  private _validateRememberCommand(memoryBank: MemoryDTO[], characterNamesInBook: string[], nodesInImport?: EpisodeTextImportNode[]) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const hasRememberCommandInSpeaker = commands.remember.has(EpisodeTextColumnKey.NodeOrSpeaker);
    const hasRememberCommandInText = commands.remember.has(EpisodeTextColumnKey.Text);
    const hasRememberCommandInDescription = commands.remember.has(EpisodeTextColumnKey.Description);

    if (hasRememberCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #REMEMBER: command cannot be used in this cell`);
      return;
    }

    // Check if REMEMBER command is used in intro node
    if (nodesInImport && (hasRememberCommandInText || hasRememberCommandInDescription)) {
      const currentNode = nodesInImport.find((node) => node.steps.some((step) => step.id === this.id));
      if (currentNode && currentNode.title.toLocaleLowerCase().trim() === 'intro') {
        const columnKey = commands.remember.keys().next().value;
        this.errors.push(`[${columnKey}] #REMEMBER: command cannot be used in intro node`);
        return;
      }
    }
    if (hasRememberCommandInText || hasRememberCommandInDescription) {
      const commandValues = commands.remember.values().next().value;
      const rawTargetName = commandValues?.[2]?.toLocaleLowerCase().trim() ?? '';
      const memoryName = stripCharacterMemoryPrefix(rawTargetName);
      const operator = commandValues?.[3]?.toLocaleLowerCase().trim() ?? '';
      // For view command: group 4 is for [brackets] format, group 5 is for non-brackets format
      const value = (commandValues?.[4]?.trim() || commandValues?.[5]?.trim()) ?? '';

      const columnKey = commands.remember.keys().next().value;

      const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
      if (!isEmpty(speakerName)) {
        this.errors.push(`[${columnKey}] #REMEMBER: Character alias cannot be used with this command`);
        return;
      }

      if (isEmpty(memoryName)) {
        this.errors.push(`[${columnKey}] #REMEMBER: Memory name does not recognized. Please check the command syntax`);
        return;
      }
      if (isEmpty(operator)) {
        this.errors.push(`[${columnKey}] #REMEMBER: Operator should be provided`);
        return;
      }

      if (operator === 'view') {
        const rawValues = (value || '').split(',').map((v: string) => v.toLocaleLowerCase().trim()).filter((v: string) => v.length > 0);

        // Check if view targets are provided
        if (rawValues.length === 0) {
          this.errors.push(`[${columnKey}] #REMEMBER: View targets must be specified. Use one of: ${Object.values(MemoryShowIn).join(', ')}`);
          return;
        }

        const hasInvalid = rawValues.some((v: string) => !Object.values(MemoryShowIn).includes(v as MemoryShowIn));
        if (hasInvalid) {
          this.errors.push(`[${columnKey}] #REMEMBER: Invalid view target. Allowed: ${Object.values(MemoryShowIn).join(', ')}`);
          return;
        }

        // Check for duplicate targets
        const uniqueValues = [...new Set(rawValues)];
        if (uniqueValues.length !== rawValues.length) {
          this.errors.push(`[${columnKey}] #REMEMBER: Duplicate view targets are not allowed`);
          return;
        }

        // Check if 'hide' is used with other targets
        const hasHide = rawValues.includes(MemoryShowIn.Hide);
        const hasOthers = rawValues.some((v: string) => v !== MemoryShowIn.Hide);
        if (hasHide && hasOthers) {
          this.errors.push(`[${columnKey}] #REMEMBER: '${MemoryShowIn.Hide}' cannot be combined with other view targets`);
          return;
        }

        // Check if memory exists for view command
        const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName);
        if (!memory) {
          this.errors.push(`[${columnKey}] #REMEMBER: Memory name does not exist in Memory Bank: ${memoryName}`);
          return;
        }

        // View command validation passed — store action
        this.action = {
          variableId: memory.id,
          type: BranchStepMemoryActionType.View,
          value: rawValues as MemoryShowIn[],
        };
        return;
      }

      if (isEmpty(value)) {
        this.errors.push(`[${columnKey}] #REMEMBER: Value should be provided`);
        return;
      }

      const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName);
      if (!memory) {
        this.errors.push(`[${columnKey}] #REMEMBER: Memory name does not exist in Memory Bank: ${memoryName}`);
        return;
      }

      const isNumber = memory.type === MemoryType.Number;
      const isBoolean = memory.type === MemoryType.Boolean;

      // Check if value references another memory variable
      const compareMemory = memoryBank.find(
        (memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === value.toLocaleLowerCase().trim(),
      );

      if (compareMemory) {
        if (compareMemory.id === memory.id) {
          this.errors.push(`[${columnKey}] #REMEMBER: Memory and referenced memory should be different`);
          return;
        }
        if (['+', '-'].includes(operator) && (memory.type !== MemoryType.Number || compareMemory.type !== MemoryType.Number)) {
          this.errors.push(`[${columnKey}] #REMEMBER: Increase/decrease requires both memories to be number type`);
          return;
        }
        if (operator === '=' && memory.type !== compareMemory.type) {
          this.errors.push(`[${columnKey}] #REMEMBER: Set requires both memories to be the same type`);
          return;
        }
      } else {
        if (!isNumber && ['+', '-'].includes(operator)) {
          this.errors.push(`[${columnKey}] #REMEMBER: Operator should be '=' for non-number memory. Operator: ${operator}`);
          return;
        }

        switch (memory.type) {
          case MemoryType.Number:
            if (Number.isNaN(Number(value))) {
              this.errors.push(`[${columnKey}] #REMEMBER: Value should be a number or an existing memory variable. Value: ${value}`);
              return;
            }
            break;
          case MemoryType.Boolean:
            if (!['yes', 'no'].includes(value.toLocaleLowerCase().trim())) {
              this.errors.push(`[${columnKey}] #REMEMBER: Value should be 'yes' or 'no' for boolean memory. Value: ${value}`);
              return;
            }
            break;
          case MemoryType.String:
            break;
          case MemoryType.Character: {
            const resolved = resolveCharacterTypeValue(value, memoryBank, characterNamesInBook);
            if (!resolved.ok) {
              this.errors.push(`[${columnKey}] #REMEMBER: ${resolved.error}`);
              return;
            }
            if (resolved.kind === 'variable') {
              this.action = {
                variableId: memory.id,
                type: BranchStepMemoryActionType.Set,
                value: { type: MemoryCheckValueType.Variable, variableId: resolved.variableId },
              } as BranchStepMemoryAction;
              return;
            }
            this.action = {
              variableId: memory.id,
              type: BranchStepMemoryActionType.Set,
              value: resolved.alias,
            } as BranchStepMemoryAction;
            return;
          }
          default:
            this.errors.push(`[${columnKey}] #REMEMBER: Value is not an existing memory variable, and target type "${memory.type}" has no literal-value validation rule. Value: ${value}`);
            return;
        }
      }

      // All validation passed — store action for diff in buildEditContextPayload
      if (this.errors.length === 0) {
        const actionTypeMap: Record<string, BranchStepMemoryActionType> = {
          '+': BranchStepMemoryActionType.Increase,
          '-': BranchStepMemoryActionType.Decrease,
          '=': BranchStepMemoryActionType.Set,
        };
        const actionType = actionTypeMap[operator];
        if (actionType) {
          if (compareMemory) {
            this.action = {
              variableId: memory.id,
              type: actionType,
              value: { type: MemoryCheckValueType.Variable, variableId: String(compareMemory.id) },
            } as BranchStepMemoryAction;
          } else {
            let resolvedValue: string | number;
            if (isBoolean) {
              resolvedValue = String(value.toLocaleLowerCase() === 'yes');
            } else if (isNumber) {
              resolvedValue = Number(value);
            } else {
              resolvedValue = value;
            }
            this.action = {
              variableId: memory.id,
              type: actionType,
              value: resolvedValue,
            } as BranchStepMemoryAction;
          }
        }
      }
    }
  }

  private _validateRandomCommand(memoryBank: MemoryDTO[]) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const hasRandomCommandInSpeaker = commands.random.has(EpisodeTextColumnKey.NodeOrSpeaker);
    const hasRandomCommandInText = commands.random.has(EpisodeTextColumnKey.Text);
    const hasRandomCommandInDescription = commands.random.has(EpisodeTextColumnKey.Description);

    if (hasRandomCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #RANDOM: command cannot be used in this cell`);
      return;
    }
    
    if (hasRandomCommandInText || hasRandomCommandInDescription) {
      const memoryName = commands.random.values().next().value?.[2]?.toLocaleLowerCase().trim() ?? '';
      const operator = commands.random.values().next().value?.[3]?.toLocaleLowerCase().trim() ?? '';

      const columnKey = commands.random.keys().next().value;

      const speakerName = EpisodeTextImportHelper.removeAllCommandsFromString(this.speakerName);
      if (!isEmpty(speakerName)) {
        this.errors.push(`[${columnKey}] #RANDOM: Character alias cannot be used with this command`);
        return;
      }

      if (isEmpty(memoryName)) {
        this.errors.push(`[${columnKey}] #RANDOM: Memory name not recognized. Please check the command syntax`);
        return;
      }
      
      if (isEmpty(operator)) {
        this.errors.push(`[${columnKey}] #RANDOM: Operator should be provided`);
        return;
      }

      const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName);
      if (!memory) {
        this.errors.push(`[${columnKey}] #RANDOM: Memory name does not exist in Memory Bank: ${memoryName}`);
        return;
      }

      if (memory.type !== MemoryType.Number) {
        this.errors.push(`[${columnKey}] #RANDOM: Random can only be used with numeric memory variables. Memory type: ${memory.type}`);
        return;
      }

      // Handle dice notation
      if (operator === 'dice') {
        const diceNotation = commands.random.values().next().value?.[4]?.trim() ?? '';

        if (isEmpty(diceNotation)) {
          this.errors.push(`[${columnKey}] #RANDOM: Dice notation should be provided`);
          return;
        }

        // Validate dice notation syntax using dice roller library
        try {
          new DiceRoll(diceNotation);
        } catch (error) {
          this.errors.push(`[${columnKey}] #RANDOM: Invalid dice notation: ${diceNotation}. ${error instanceof Error ? error.message : 'Unknown error'}`);
          return;
        }

        // Store parsed roll on model for diff in buildEditContextPayload
        this.roll = {
          variableId: memory.id,
          type: BranchStepMemoryRollType.Dice,
          diceNotation,
        };
      } else {
        // Handle range-based random [min, max] modifier value
        const minValue = commands.random.values().next().value?.[4]?.trim() ?? '';
        const maxValue = commands.random.values().next().value?.[5]?.trim() ?? '';
        const modifierValue = commands.random.values().next().value?.[6]?.trim();

        if (isEmpty(minValue)) {
          this.errors.push(`[${columnKey}] #RANDOM: Min value should be provided`);
          return;
        }

        if (isEmpty(maxValue)) {
          this.errors.push(`[${columnKey}] #RANDOM: Max value should be provided`);
          return;
        }

        if (Number.isNaN(Number(minValue))) {
          this.errors.push(`[${columnKey}] #RANDOM: Min value should be a number. Value: ${minValue}`);
          return;
        }

        if (Number.isNaN(Number(maxValue))) {
          this.errors.push(`[${columnKey}] #RANDOM: Max value should be a number. Value: ${maxValue}`);
          return;
        }

        const min = Number(minValue);
        const max = Number(maxValue);

        if (min > max) {
          this.errors.push(`[${columnKey}] #RANDOM: Min value cannot be greater than max value. Min: ${min}, Max: ${max}`);
          return;
        }

        // Validate modifier if provided
        if (modifierValue !== undefined && !isEmpty(modifierValue)) {
          if (Number.isNaN(Number(modifierValue))) {
            this.errors.push(`[${columnKey}] #RANDOM: Modifier should be a number. Value: ${modifierValue}`);
            return;
          }
        }

        // Validate operator
        if (!['+', '-', '='].includes(operator)) {
          this.errors.push(`[${columnKey}] #RANDOM: Operator should be '=', '+', or '-'. Operator: ${operator}`);
          return;
        }

        // Store parsed roll on model for diff in buildEditContextPayload
        const rollTypeMap: Record<string, BranchStepMemoryRollType> = {
          '+': BranchStepMemoryRollType.Increase,
          '-': BranchStepMemoryRollType.Decrease,
          '=': BranchStepMemoryRollType.Set,
        };
        const modifier = modifierValue !== undefined && !isEmpty(modifierValue) ? Number(modifierValue) : undefined;
        this.roll = {
          variableId: memory.id,
          type: rollTypeMap[operator],
          min: String(min),
          max: String(max),
          ...(modifier !== undefined && { modifier }),
        } as BranchStepMemoryRoll;
      }
    }
  }

  private _validateCheckCommand(memoryBank: MemoryDTO[], characterNamesInBook: string[], nodesInImport: EpisodeTextImportNode[], nodesInEpisode: Branch[], rewardIds?: Map<string, number>) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const hasCheckCommandInSpeaker = commands.check.has(EpisodeTextColumnKey.NodeOrSpeaker);

    if (hasCheckCommandInSpeaker) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] #CHECK: command cannot be used in this cell`);
      return;
    }
    const columnKey = commands.check.keys().next().value;

    if (!columnKey) {
      return;
    }

    const checkEntityType = EpisodeTextImportHelper.getCheckEntityTypes(this.commands);
    
    const currentNode = nodesInImport.find((node) => node.steps.some((step) => step.id === this.id));
    if (currentNode) {
      const currentStepIndex = currentNode.steps.findIndex((step) => step.id === this.id);
      const prevStep = currentNode.steps[currentStepIndex - 1];
      const nextStep = currentNode.steps[currentStepIndex + 1];
      if (prevStep) {
        this.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Check command should be the first step in the node`);
        return;
      }
      
      // For condition list checks, allow CheckCondition steps to follow
      if (checkEntityType === 'conditionList') {
        // Condition list checks should have condition items following, but they're stored separately
        // in conditionListItems array, not as regular steps, so no validation needed here
      } else if (nextStep) {
        // For single and multi (min/max) checks, there should be no next step
        this.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: The "check" step type must be the only type in the node`);
        return;
      }
    }

    const nodeNamesInImport = nodesInImport.map((node) => node.title);
    const nodeNames = [...nodeNamesInImport, ...nodesInEpisode.map((node) => node.title)];

    if (checkEntityType === 'playerHas') {
      const {
        matchType,
        rewardNames,
        trueGoto,
        falseGoto,
      } = EpisodeTextImportHelper.getPlayerHasCheckCommandValues(this.commands);

      if (rewardNames.length === 0) {
        this.errors.push(`[${columnKey}] #CHECK: Reward names are missing. Expected format: PLAYERHAS ALL/ANY (reward1, reward2)`);
        return;
      }

      // Validate that reward names exist in achievements and resolve to IDs
      if (rewardIds && rewardIds.size > 0) {
        const unknownRewards = rewardNames.filter(
          (name: string) => !rewardIds.has(name.toLocaleLowerCase().trim()),
        );
        if (unknownRewards.length > 0) {
          this.errors.push(
            `[${columnKey}] #CHECK: Achievement${unknownRewards.length > 1 ? 's' : ''} not found: ${unknownRewards.join(', ')}. Check spelling or create the achievement first`,
          );
          return;
        }
      }

      if (isEmpty(trueGoto)) {
        this.errors.push(`[${columnKey}] #CHECK: Missing "if Yes" goto node. Expected format: PLAYERHAS ALL (rewards), if Yes goto node_true, if No goto node_false`);
        return;
      }

      if (!EpisodeTextImportHelper.isNameExists(trueGoto, nodeNames)) {
        this.errors.push(`[${columnKey}] #CHECK: "if Yes" goto node does not exist: "${trueGoto}"`);
        return;
      }

      if (isEmpty(falseGoto)) {
        this.errors.push(`[${columnKey}] #CHECK: Missing "if No" goto node. Expected format: PLAYERHAS ALL (rewards), if Yes goto node_true, if No goto node_false`);
        return;
      }

      if (!EpisodeTextImportHelper.isNameExists(falseGoto, nodeNames)) {
        this.errors.push(`[${columnKey}] #CHECK: "if No" goto node does not exist: "${falseGoto}"`);
        return;
      }

      if (currentNode) {
        if (currentNode.title.toLocaleLowerCase().trim() === trueGoto.toLocaleLowerCase().trim()) {
          this.errors.push(`[${columnKey}] #CHECK: "if Yes" goto cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
          return;
        }
        if (currentNode.title.toLocaleLowerCase().trim() === falseGoto.toLocaleLowerCase().trim()) {
          this.errors.push(`[${columnKey}] #CHECK: "if No" goto cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
          return;
        }
      }

      // Resolve reward names to IDs if map is available, otherwise store names as-is
      const resolvedRewardIds = rewardIds && rewardIds.size > 0
        ? rewardNames.map((name: string) => rewardIds.get(name.toLocaleLowerCase().trim()) ?? -1)
        : [];

      this.check = {
        variableId: 0,
        operator: StepCheckOperatorType.PlayerHas,
        value: resolvedRewardIds,
        match: matchType === 'any' ? PlayerHasMatchType.Any : PlayerHasMatchType.All,
      };

      this.switch = [
        {
          value: true,
          gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === trueGoto)?.id ?? -1,
        },
        {
          value: false,
          gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === falseGoto)?.id ?? -1,
        },
      ];
      return;
    }

    if (checkEntityType === 'none') {
      this.errors.push(`[${columnKey}] #CHECK: Error in operation syntax. Please check the command syntax`);
      return;
    }

    if (checkEntityType === 'single') {
      const {
        singleCheckTypeMemoryName,
        singleCheckTypeOperator,
        singleCheckTypeTextOperator,
        singleCheckTypeValue,
        singleCheckTypeTrueGoto,
        singleCheckTypeFalseGoto,
      } = EpisodeTextImportHelper.getSingleCheckCommandValues(this.commands);

      if (isEmpty(singleCheckTypeMemoryName)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in memory name. Please check the command syntax`);
        return;
      }

      const isEmptyOperator = isEmpty(singleCheckTypeOperator) && isEmpty(singleCheckTypeTextOperator);

      if (isEmptyOperator) {
        this.errors.push(`[${columnKey}] #CHECK: Error in operator. Please check the command syntax`);
        return;
      }

      if (isEmpty(singleCheckTypeValue)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in value. Please check the command syntax`);
        return;
      }

      if (isEmpty(singleCheckTypeTrueGoto)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in True goto. Please check the command syntax`);
        return;
      }

      if (!EpisodeTextImportHelper.isNameExists(singleCheckTypeTrueGoto, nodeNames)) {
        this.errors.push(`[${columnKey}] #CHECK: True goto node does not exist. Node: ${singleCheckTypeTrueGoto}`);
        return;
      }

      if (isEmpty(singleCheckTypeFalseGoto)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in false goto. Please check the command syntax`);
        return;
      }

      if (!EpisodeTextImportHelper.isNameExists(singleCheckTypeFalseGoto, nodeNames)) {
        this.errors.push(`[${columnKey}] #CHECK: False goto node does not exist. Node: ${singleCheckTypeFalseGoto}`);
        return;
      }

      if (currentNode) {
        if (currentNode.title.toLocaleLowerCase().trim() === singleCheckTypeTrueGoto.toLocaleLowerCase().trim()) {
          this.errors.push(`[${columnKey}] #CHECK: True goto cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
          return;
        }
        if (currentNode.title.toLocaleLowerCase().trim() === singleCheckTypeFalseGoto.toLocaleLowerCase().trim()) {
          this.errors.push(`[${columnKey}] #CHECK: False goto cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
          return;
        }
      }

      const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === singleCheckTypeMemoryName);
      if (!memory) {
        this.errors.push(`[${columnKey}] #CHECK: Memory name does not exist in Memory Bank: ${singleCheckTypeMemoryName}`);
        return;
      }

      const operators = [
        StepCheckOperatorType.Equal,
        StepCheckOperatorType.NotEqual,
        StepCheckOperatorType.Greater,
        StepCheckOperatorType.AtLeast,
        StepCheckOperatorType.Less,
        StepCheckOperatorType.AtMost,
      ];

      const allowedNonNumericOperators = [StepCheckOperatorType.Equal, StepCheckOperatorType.NotEqual];

      const operator = operators.find((operatorKey) => {
        const checkOperator = CheckOperator[operatorKey];
        return checkOperator.short === singleCheckTypeOperator || checkOperator.title.toLocaleLowerCase() === singleCheckTypeTextOperator;
      });

      if (!operator) {
        this.errors.push(`[${columnKey}] #CHECK: Operator is not recognized. Please check the command syntax`);
        return;
      }

      if (memory.type !== MemoryType.Number && !allowedNonNumericOperators.includes(operator)) {
        this.errors.push(`[${columnKey}] #CHECK: Operator should be '=' or '<>' for boolean memory. Operator: ${singleCheckTypeOperator ?? singleCheckTypeTextOperator}`);
        return;
      }

      if (memory.type === MemoryType.Character) {
        const resolved = resolveCharacterTypeValue(singleCheckTypeValue, memoryBank, characterNamesInBook);
        if (!resolved.ok) {
          this.errors.push(`[${columnKey}] #CHECK: ${resolved.error}`);
          return;
        }
        const checkValue = resolved.kind === 'variable'
          ? { type: MemoryCheckValueType.Variable, variableId: resolved.variableId } as MemorySingleLink
          : resolved.alias;
        this.check = {
          variableId: memory.id,
          operator,
          value: checkValue,
        };
        this.switch = [
          {
            value: true,
            gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === singleCheckTypeTrueGoto)?.id ?? -1,
          },
          {
            value: false,
            gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === singleCheckTypeFalseGoto)?.id ?? -1,
          },
        ];
        return;
      }

      const compareMemory = memoryBank.find(
        (memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === String(singleCheckTypeValue).toLocaleLowerCase().trim(),
      );

      // Validate that numeric memory values are actually numbers (or existing memory variable names)
      if (memory.type === MemoryType.Number && !compareMemory && isNaN(Number(singleCheckTypeValue))) {
        this.errors.push(`[${columnKey}] #CHECK: Value should be a number or an existing memory variable for numeric memory. Value: ${singleCheckTypeValue}`);
        return;
      }

      const isNotYesOrNoValue = singleCheckTypeValue.toLocaleLowerCase() !== 'yes' && singleCheckTypeValue.toLocaleLowerCase() !== 'no';
      const isCompereBooleanMemory = memory.type === MemoryType.Boolean && isNotYesOrNoValue;
      const isCompareMemory = !!compareMemory || isCompereBooleanMemory;

      if (isCompareMemory) {
        if (!compareMemory) {
          this.errors.push(`[${columnKey}] #CHECK: Compare memory name does not exist in Memory Bank: ${singleCheckTypeValue}`);
          return;
        }
        if (memory.id === compareMemory.id) {
          this.errors.push(`[${columnKey}] #CHECK: Memory and compare memory should be different.`);
          return;
        }

        if (memory.type !== compareMemory.type) {
          this.errors.push(`[${columnKey}] #CHECK: Memory and compare memory should be the same type. Memory: ${memory.type}, Compare memory: ${compareMemory.type}`);
          return;
        }
      }

      if (memory.type === MemoryType.Boolean && !isCompereBooleanMemory) {
        if (!['yes', 'no'].includes(singleCheckTypeValue.toLocaleLowerCase().trim())) {
          this.errors.push(`[${columnKey}] #CHECK: Value should be 'yes' or 'no' for boolean memory. Value: ${singleCheckTypeValue}`);
          return;
        }
      }

      if (isCompareMemory || isCompereBooleanMemory) {
        const value: MemorySingleLink = {
          type: MemoryCheckValueType.Variable,
          variableId: memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === singleCheckTypeValue.toLocaleLowerCase().trim())?.id.toString() ?? '',
        };

        this.check = {
          variableId: memory.id,
          operator,
          value,
        };
      } else {
        this.check = {
          variableId: memory.id,
          operator,
          value: memory.type === MemoryType.Boolean
            ? String(singleCheckTypeValue.toLocaleLowerCase() === 'yes')
            : singleCheckTypeValue,
        };
      }
      this.switch = [
        {
          value: true,
          gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === singleCheckTypeTrueGoto)?.id ?? -1,
        },
        {
          value: false,
          gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === singleCheckTypeFalseGoto)?.id ?? -1,
        },
      ];
    }

    if (checkEntityType === 'multi') {
      const {
        multiCheckTypeFuncName,
        multiCheckTypeGotoTextValues,
        multiCheckTypeGotoValues,
        multiCheckTypeMemoryNames,
        multiCheckTypeMemoryTextNames,
        multiCheckTypeSwitchMemoryNames,
      } = EpisodeTextImportHelper.getMultiCheckCommandValues(this.commands);

      if (isEmpty(multiCheckTypeFuncName)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in function name. Please check the command syntax`);
        return;
      }

      if (isEmpty(multiCheckTypeMemoryTextNames) || isEmpty(multiCheckTypeMemoryNames)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in memory names. Please check the command syntax`);
        return;
      }

      if (isEmpty(multiCheckTypeGotoTextValues) || isEmpty(multiCheckTypeGotoValues)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in goto values. Please check the command syntax`);
        return;
      }

      for (let i = 0; i < multiCheckTypeGotoValues.length; i++) {
        const gotoValue = multiCheckTypeGotoValues[i];
        if (!EpisodeTextImportHelper.isNameExists(gotoValue, nodeNames)) {
          this.errors.push(`[${columnKey}] #CHECK: Goto node does not exist. Node: ${gotoValue}`);
          return;
        }
      }

      if (currentNode) {
        const selfReferenceGoto = multiCheckTypeGotoValues.find(
          (gotoValue) => currentNode.title.toLocaleLowerCase().trim() === gotoValue.toLocaleLowerCase().trim(),
        );
        if (selfReferenceGoto) {
          this.errors.push(
            `[${columnKey}] #CHECK: Goto node cannot reference the current node "${currentNode.title}" as it would create an infinite loop`,
          );
          return;
        }
      }

      if (multiCheckTypeMemoryNames.length < 1) {
        this.errors.push(`[${columnKey}] #CHECK: Memory names should be 2 or more`);
        return;
      }

      if (multiCheckTypeGotoValues.length < 2) {
        this.errors.push(`[${columnKey}] #CHECK: Goto values should be 2 or more`);
        return;
      }

      if (multiCheckTypeMemoryNames.length !== multiCheckTypeGotoValues.length) {
        this.errors.push(`[${columnKey}] #CHECK: Memory and Goto values should be equal in count`);
        return;
      }

      for (let i = 0; i < multiCheckTypeMemoryNames.length; i++) {
        const memoryName = multiCheckTypeMemoryNames[i];
        const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName);
        if (!memory) {
          this.errors.push(`[${columnKey}] #CHECK: Memory name does not exist in Memory Bank: ${memoryName}`);
          return;
        }
        if (memory.type !== MemoryType.Number) {
          this.errors.push(`[${columnKey}] #CHECK: Memory should be a numeric type. Memory: ${memoryName}`);
          return;
        }
      }

      // Validate IF switch variable names — must exist in Memory Bank and match header memory names
      for (let i = 0; i < multiCheckTypeSwitchMemoryNames.length; i++) {
        const switchMemoryName = multiCheckTypeSwitchMemoryNames[i];
        if (isEmpty(switchMemoryName)) {
          this.errors.push(`[${columnKey}] #CHECK: Missing variable name in IF clause at position ${i + 1}`);
          return;
        }
        const switchMemory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === switchMemoryName);
        if (!switchMemory) {
          this.errors.push(`[${columnKey}] #CHECK: Variable in IF clause does not exist in Memory Bank: ${switchMemoryName}`);
          return;
        }
        if (!multiCheckTypeMemoryNames.includes(switchMemoryName)) {
          this.errors.push(`[${columnKey}] #CHECK: Variable in IF clause must match one of the header variables: ${switchMemoryName}`);
          return;
        }
      }

      const operators = [
        StepCheckOperatorType.Min,
        StepCheckOperatorType.Max,
      ];

      const operator = operators.find((operatorKey) => {
        const checkOperator = CheckOperator[operatorKey];
        return checkOperator.short.toLocaleLowerCase().trim() === multiCheckTypeFuncName
          || checkOperator.title.toLocaleLowerCase().trim() === multiCheckTypeFuncName;
      });

      if (!operator) {
        this.errors.push(`[${columnKey}] #CHECK: Operator is not recognized. Please check the command syntax`);
        return;
      }

      const validateIds = multiCheckTypeMemoryNames.map((memoryName: string) => memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName)?.id.toString() ?? '');

      const value: MemoryArrayLink = {
        type: MemoryCheckValueType.VariableArray,
        variableId: validateIds,
      };

      this.check = {
        variableId: -1,
        operator,
        value,
      };

      this.switch = multiCheckTypeMemoryNames.map((memoryName: string, index: number) => ({
        value: memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName)?.id.toString() ?? '',
        gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === multiCheckTypeGotoValues?.[index])?.id ?? -1,
      }));
    }

    if (checkEntityType === 'conditionList') {
      const {
        conditionListMemoryName,
      } = EpisodeTextImportHelper.getConditionListCheckCommandValues(this.commands);

      if (isEmpty(conditionListMemoryName)) {
        this.errors.push(`[${columnKey}] #CHECK: Error in memory name. Please check the command syntax`);
        return;
      }

      const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === conditionListMemoryName);
      if (!memory) {
        this.errors.push(`[${columnKey}] #CHECK: Memory name does not exist in Memory Bank: ${conditionListMemoryName}`);
        return;
      }

      // Validate conditions
      const conditions = this.conditionListItems;
      
      if (conditions.length < 1 || conditions.length > 10) {
        this.errors.push(`[${columnKey}] #CHECK: Condition list must have 1-10 conditions, got ${conditions.length}`);
        return;
      }

      // Check for else clause
      const hasElse = conditions.some((cond) => cond.speakerText.trim().toLowerCase().startsWith('else'));
      if (!hasElse) {
        this.errors.push(`[${columnKey}] #CHECK: Condition list requires an 'else' clause`);
        return;
      }

      // Validate each condition
      const switchCases: StepCheckSwitch[] = [];
      const seenConditions = new Set<string>();

      for (let i = 0; i < conditions.length; i++) {
        const condition = conditions[i];
        const conditionText = condition.speakerText.trim();
        
        // Parse condition: "if <operator> <value>" or "else"
        if (conditionText.toLowerCase().startsWith('else')) {
          // Else clause - should be last
          if (i !== conditions.length - 1) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: 'else' clause must be the last condition`);
          }
          
          // Get goto from description
          const gotoMatch = condition.description.match(/#(?:GOTO|->)\s+(\w+)/i);
          const gotoNode = gotoMatch?.[1] ?? '';
          
          if (isEmpty(gotoNode)) {
            condition.errors.push(`[${EpisodeTextColumnKey.Description}] #CHECK: 'else' clause requires a #goto command`);
          } else if (!EpisodeTextImportHelper.isNameExists(gotoNode, nodeNames)) {
            condition.errors.push(`[${EpisodeTextColumnKey.Description}] #CHECK: Goto node does not exist: ${gotoNode}`);
          } else {
            // Else clause (no value field for conditionList)
            switchCases.push({
              gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === gotoNode.toLocaleLowerCase().trim())?.id ?? -1,
            });
          }
        } else {
          // Regular condition: "if <operator> <value>"
          const match = conditionText.match(/^if\s+(=|<>|>|>=|<|<=)\s+(.+)$/i);
          
          if (!match) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Invalid condition format. Expected 'if <operator> <value>'`);
            continue;
          }
          
          const operatorSymbol = match[1].trim();
          const value = match[2].trim();
          
          // Find operator
          const operators = [
            StepCheckOperatorType.Equal,
            StepCheckOperatorType.NotEqual,
            StepCheckOperatorType.Greater,
            StepCheckOperatorType.AtLeast,
            StepCheckOperatorType.Less,
            StepCheckOperatorType.AtMost,
          ];
          
          const operator = operators.find((op) => CheckOperator[op].short === operatorSymbol);
          
          if (!operator) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Unknown operator: ${operatorSymbol}`);
            continue;
          }
          
          // Check for duplicate conditions
          const conditionKey = `${operator}:${value.toLowerCase()}`;
          if (seenConditions.has(conditionKey)) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Duplicate condition: if ${operatorSymbol} ${value}`);
            continue;
          }
          seenConditions.add(conditionKey);
          
          // Get goto from description
          const gotoMatch = condition.description.match(/#(?:GOTO|->)\s+(\w+)/i);
          const gotoNode = gotoMatch?.[1] ?? '';
          
          if (isEmpty(gotoNode)) {
            condition.errors.push(`[${EpisodeTextColumnKey.Description}] #CHECK: Condition requires a #goto command`);
            continue;
          }
          
          if (!EpisodeTextImportHelper.isNameExists(gotoNode, nodeNames)) {
            condition.errors.push(`[${EpisodeTextColumnKey.Description}] #CHECK: Goto node does not exist: ${gotoNode}`);
            continue;
          }
          
          // Check if value is a variable or number
          const isNumber = !Number.isNaN(Number(value));
          const compareMemory = memoryBank.find((m) => m.name.toLocaleLowerCase().trim() === value.toLocaleLowerCase().trim());
          
          if (!isNumber && !compareMemory) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Value must be a number or an existing memory variable: ${value}`);
            continue;
          }
          
          // Check for self-comparison
          if (compareMemory && compareMemory.id === memory.id) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Cannot compare memory to itself`);
            continue;
          }
          
          // Check type compatibility if comparing to variable
          if (compareMemory && memory.type !== compareMemory.type) {
            condition.errors.push(`[${EpisodeTextColumnKey.Text}] #CHECK: Memory types must match. ${memory.name}: ${memory.type}, ${compareMemory.name}: ${compareMemory.type}`);
            continue;
          }
          
          // Build switch case (without value field for conditionList)
          const switchCase: any = {
            operator,
            gotoBranchId: nodesInEpisode.find((node) => node.title.toLocaleLowerCase().trim() === gotoNode.toLocaleLowerCase().trim())?.id ?? -1,
          };
          
          if (compareMemory) {
            switchCase.variable = compareMemory.id;
          } else {
            switchCase.number = value;
          }
          
          switchCases.push(switchCase);
        }
        
        // Propagate condition errors to parent step
        if (condition.errors.length > 0) {
          this.errors.push(...condition.errors);
        }
      }
      
      // Check for self-references
      if (currentNode) {
        const selfReferenceCondition = conditions.find((cond) => {
          const gotoMatch = cond.description.match(/#(?:GOTO|->)\s+(\w+)/i);
          const gotoNode = gotoMatch?.[1] ?? '';
          return currentNode.title.toLocaleLowerCase().trim() === gotoNode.toLocaleLowerCase().trim();
        });
        
        if (selfReferenceCondition) {
          this.errors.push(`[${columnKey}] #CHECK: Goto node cannot reference the current node "${currentNode.title}" as it would create an infinite loop`);
          return;
        }
      }

      // Set check and switch data (no value field for conditionList)
      this.check = {
        variableId: memory.id,
        operator: StepCheckOperatorType.ConditionList,
      };
      
      this.switch = switchCases;
    }
  }

  private _validateReqCommand(memoryBank: MemoryDTO[], characterNamesInBook: string[]) {
    if (!this.commands.has(EpisodeTextImportCommandKey.StepReq)) {
      return;
    }

    const reqCommand = this.commands.get(EpisodeTextImportCommandKey.StepReq);
    const columnKey = reqCommand?.keys().next().value;

    if (!columnKey) {
      return;
    }

    // Check for duplicate #REQ/#REQUIREMENT in the same cell
    const cellText = columnKey === EpisodeTextColumnKey.Description ? this.description : this.speakerText;
    const reqOccurrences = (cellText.match(/#REQ(?:UIREMENT)?[:\s]/gi) ?? []).length;
    if (reqOccurrences > 1) {
      this.errors.push(`[${columnKey}] #REQ: Duplicate requirement command in answer row`);
      return;
    }

    const commandValues = reqCommand?.values().next().value;
    const remainder = commandValues?.[2]?.trim() ?? '';

    if (isEmpty(remainder)) {
      this.errors.push(`[${columnKey}] #REQ: Memory slot name is required`);
      return;
    }

    // Parse comma-separated parts:
    // parts[0] — "memoryName operator value" (required)
    // parts[1] — "otherwise Hide|Disable" or "Hide|Disable" (optional)
    // parts[2] — "Hide indicator" / "Show indicator" / "HideIndicator" / "ShowIndicator" (optional, requires parts[1])
    const parts = remainder.split(',').map((p: string) => p.trim());

    if (!/^((?:memory\.)?\w+)\s*(=|<>|>=|<=|<|>)\s*(-?(?:memory\.)?\w+)$/i.test(parts[0])) {
      this.errors.push(`[${columnKey}] #REQ: Invalid syntax. Expected: memoryName operator value`);
      return;
    }

    const { reqMemoryName: memoryName, reqOperatorSymbol: operatorSymbol, reqValue: value } = parseReqCommand(remainder);

    // Parse optional otherwise (parts[1])
    let otherwiseValue = '';
    let indicatorPartIndex = 1;
    const validIndicatorPattern = /^(hide\s*indicator|show\s*indicator|hideindicator|showindicator)$/i;

    if (parts.length > 1) {
      const otherwiseRaw = parts[1].replace(/^otherwise\s+/i, '').trim();
      if (['hide', 'disable'].includes(otherwiseRaw.toLowerCase())) {
        otherwiseValue = otherwiseRaw;
        indicatorPartIndex = 2;
      } else if (validIndicatorPattern.test(parts[1])) {
        // Indicator provided without otherwise
        this.errors.push(`[${columnKey}] #REQ: When specifying indicator visibility, 'otherwise Hide' or 'otherwise Disable' must also be provided`);
        return;
      } else {
        this.errors.push(`[${columnKey}] #REQ: Unknown 'otherwise' value: '${parts[1]}'. Expected 'Hide' or 'Disable'`);
        return;
      }
    }

    // Parse optional indicator (parts[indicatorPartIndex])
    let indicatorValue = '';
    if (parts.length > indicatorPartIndex) {
      if (!validIndicatorPattern.test(parts[indicatorPartIndex])) {
        this.errors.push(`[${columnKey}] #REQ: Unknown indicator value: '${parts[indicatorPartIndex]}'. Expected 'Hide indicator' or 'Show indicator'`);
        return;
      }
      indicatorValue = parts[indicatorPartIndex].trim();
    }

    // Validate memory slot
    const memory = memoryBank.find((memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === memoryName);
    if (!memory) {
      this.errors.push(`[${columnKey}] #REQ: Memory slot does not exist in Memory Bank: ${memoryName}`);
      return;
    }

    // Validate operator for memory type
    const allowedNonNumericOperators = ['=', '<>'];
    if (memory.type !== MemoryType.Number && !allowedNonNumericOperators.includes(operatorSymbol)) {
      this.errors.push(`[${columnKey}] #REQ: Operator '${operatorSymbol}' is not valid for ${memory.type} memory type. Only '=' and '<>' are allowed`);
      return;
    }

    if (memory.type === MemoryType.Character) {
      const resolved = resolveCharacterTypeValue(value, memoryBank, characterNamesInBook);
      if (!resolved.ok) {
        this.errors.push(`[${columnKey}] #REQ: ${resolved.error}`);
        return;
      }
    }

    // Check if value is a variable reference (another memory slot)
    const compareMemory = memoryBank.find(
      (memoryItem) => memoryItem.name.toLocaleLowerCase().trim() === value.toLocaleLowerCase().trim(),
    );

    if (compareMemory) {
      if (memory.id === compareMemory.id) {
        this.errors.push(`[${columnKey}] #REQ: Memory slot and compare value should be different`);
        return;
      }
      if (memory.type !== compareMemory.type) {
        this.errors.push(`[${columnKey}] #REQ: Memory types must match. ${memory.name}: ${memory.type}, ${compareMemory.name}: ${compareMemory.type}`);
        return;
      }
    } else {
      if (memory.type === MemoryType.Number && Number.isNaN(Number(value))) {
        this.errors.push(`[${columnKey}] #REQ: Value must be a number or an existing memory variable: ${value}`);
        return;
      }
      if (memory.type === MemoryType.Boolean && !['yes', 'no'].includes(value.toLocaleLowerCase())) {
        this.errors.push(`[${columnKey}] #REQ: Value should be 'yes' or 'no' for boolean memory. Value: ${value}`);
        return;
      }
    }

    // Paid answer restrictions
    const isPremiumAnswer = this.commands.has(EpisodeTextImportCommandKey.PremiumAnswer);
    if (isPremiumAnswer) {
      if (otherwiseValue.toLowerCase() === 'disable') {
        this.errors.push(`[${columnKey}] #REQ: Paid answers cannot use 'otherwise Disable'`);
        return;
      }
      const isShowIndicator = /^show\s*indicator$|^showindicator$/i.test(indicatorValue);
      if (isShowIndicator) {
        this.errors.push(`[${columnKey}] #REQ: Paid answers cannot use 'Show indicator' — indicator is always hidden`);
        return;
      }
    }
  }

  private _validateEndingCommand(nodesInImport: EpisodeTextImportNode[]) {
    const commands = EpisodeTextImportHelper.convertCommandsToRecordType(this.commands);
    const endingCommandInColumnKey = commands.ending.keys().next().value;

    if (endingCommandInColumnKey === undefined) {
      return;
    }

    const currentNode = nodesInImport.find((node) => node.steps.some((step) => step.id === this.id));
    // check if all steps is ending
    const isAllStepsEnding = currentNode?.steps.some((step) => step.type !== EpisodeTextEntityType.Ending);
    if (isAllStepsEnding) {
      this.errors.push(`[${endingCommandInColumnKey}] #ENDING: The command must be the only command type in the ENDING node.`);
    }
  }

  private _validateInvalidCommands() {
    const invalidCommandInTitle = EpisodeTextImportHelper.getInvalidValidCommand(this.speakerName);
    if (invalidCommandInTitle) {
      this.errors.push(`[${EpisodeTextColumnKey.NodeOrSpeaker}] Invalid command: ${invalidCommandInTitle}`);
    }
    const invalidCommandInText = EpisodeTextImportHelper.getInvalidValidCommand(this.speakerText);
    if (invalidCommandInText) {
      this.errors.push(`[${EpisodeTextColumnKey.Text}] Invalid command: ${invalidCommandInText}`);
    }
    const invalidCommandInDescription = EpisodeTextImportHelper.getInvalidValidCommand(this.description);
    if (invalidCommandInDescription) {
      this.errors.push(`[${EpisodeTextColumnKey.Description}] Invalid command: ${invalidCommandInDescription}`);
    }
  }
}
